import { useEffect, useState } from "react";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import {
  doc,
  setDoc,
  updateDoc,
  serverTimestamp,
  collection,
  addDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  increment,
} from "firebase/firestore";
import { storage, db } from "../../firebase/firebase";
import { useAuth } from "../../context/AuthContext";

const FREE_UPLOAD_LIMIT = 2;
const PAID_UPLOAD_LIMIT = 5;

const FREE_ANALYZE_LIMIT = 2;
const PAID_ANALYZE_LIMIT = 5;

function Pitch() {
  const { user } = useAuth();

  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const [currentPitch, setCurrentPitch] = useState(null);
  const [analysisJob, setAnalysisJob] = useState(null);

  const [plan, setPlan] = useState(null);
  const [planLoading, setPlanLoading] = useState(true);

  const [remainingUploads, setRemainingUploads] = useState(0);
  const [remainingAnalyses, setRemainingAnalyses] = useState(0);

  const [message, setMessage] = useState("");
  const [analyzing, setAnalyzing] = useState(false);

  const currentMonth = new Date().toISOString().slice(0, 7);

  /* =========================
     LIVE USER LISTENER
  ========================= */
  useEffect(() => {
    if (!user) return;

    const unsub = onSnapshot(doc(db, "users", user.uid), (snap) => {
      if (!snap.exists()) {
        setPlan("free");
        setRemainingUploads(0);
        setRemainingAnalyses(0);
        setPlanLoading(false);
        return;
      }

      const data = snap.data();
      const userPlan = data.plan || "free";
      const uploadsUsed = data.monthlyPitchUploads || 0;
      const analysesUsed = data.monthlyAnalysisCount || 0;

      const uploadLimit =
        userPlan === "paid" ? PAID_UPLOAD_LIMIT : FREE_UPLOAD_LIMIT;
      const analyzeLimit =
        userPlan === "paid" ? PAID_ANALYZE_LIMIT : FREE_ANALYZE_LIMIT;

      setPlan(userPlan);
      setRemainingUploads(Math.max(uploadLimit - uploadsUsed, 0));
      setRemainingAnalyses(Math.max(analyzeLimit - analysesUsed, 0));
      setPlanLoading(false);
    });

    return () => unsub();
  }, [user]);

  /* =========================
     LIVE PITCH LISTENER
  ========================= */
  useEffect(() => {
    if (!user) return;

    const unsub = onSnapshot(doc(db, "pitches", user.uid), (snap) => {
      setCurrentPitch(snap.exists() ? snap.data() : null);
    });

    return () => unsub();
  }, [user]);

  /* =========================
     LIVE ANALYSIS JOB LISTENER
  ========================= */
  useEffect(() => {
    if (!user) return;

    const q = query(
      collection(db, "analysisJobs"),
      where("userId", "==", user.uid),
      orderBy("createdAt", "desc"),
      limit(1)
    );

    const unsub = onSnapshot(q, (snap) => {
      if (snap.empty) {
        setAnalysisJob(null);
        setAnalyzing(false);
        return;
      }

      const job = { id: snap.docs[0].id, ...snap.docs[0].data() };
      setAnalysisJob(job);

      const running =
        job.status === "queued" || job.status === "processing";
      setAnalyzing(running);

      if (job.status === "completed") {
        setMessage("✅ Pitch analysis completed.");
      }

      if (job.status === "blocked") {
        if (job.error === "CONTENT_BLOCKED_NSFW") {
          setMessage(
            "🚫 Pitch contains explicit or abusive content. Please upload a clean, professional pitch deck."
          );
        } else if (job.error === "CONTENT_UNPROCESSABLE") {
          setMessage(
            <>
              <strong>🚫 Pitch could not be analyzed</strong>
              <br /><br />
              This pitch deck contains no readable text.
              <br /><br />
              Your file appears to be image-based.
              <br />
              Prufyy analyzes <strong>text-based PDFs only</strong>.
              <br /><br />
              <strong>How to fix this:</strong>
              <br />• Export slides with selectable text
              <br />• Save Google Slides / PowerPoint as PDF
              <br />• Upload a text-heavy notes version
            </>
          );
        } else if (job.error === "ANALYSIS_LIMIT_REACHED") {
          setMessage(
            "⚠️ Monthly analysis limit reached. Upgrade to analyze more pitches."
          );
        } else {
          setMessage("🚫 Pitch analysis blocked.");
        }
      }

      if (job.status === "failed") {
        setMessage("❌ Pitch analysis failed. Please try again later.");
      }
    });

    return () => unsub();
  }, [user]);

  /* =========================
     UPLOAD PITCH
  ========================= */
  const handleUpload = async () => {
    if (!file) {
      setMessage("Please select a pitch file first.");
      return;
    }

    const MAX_SIZE = 10 * 1024 * 1024;
    if (file.size > MAX_SIZE) {
      setMessage("Pitch deck file must be smaller than 10 MB.");
      return;
    }

    if (remainingUploads <= 0) {
      setMessage("You’ve reached your monthly upload limit.");
      return;
    }

    try {
      setUploading(true);
      setMessage("⏳ Uploading pitch…");

      const storageRef = ref(
        storage,
        `pitches/${user.uid}/${Date.now()}_${file.name}`
      );

      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);

      await setDoc(doc(db, "pitches", user.uid), {
        userId: user.uid,
        currentFileName: file.name,
        currentFileUrl: downloadURL,
        updatedAt: serverTimestamp(),
      });

      await updateDoc(doc(db, "users", user.uid), {
        monthlyPitchUploads: increment(1),
        lastUploadMonth: currentMonth,
      });

      setFile(null);
      setMessage("✅ Pitch uploaded successfully.");
    } catch {
      setMessage("❌ Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  /* =========================
     START ANALYSIS
  ========================= */
  const analyzePitch = async () => {
    if (!currentPitch) return;

    if (remainingAnalyses <= 0) {
      setMessage(
        plan === "free"
          ? "Upgrade to analyze more pitches."
          : "Monthly analysis limit reached."
      );
      return;
    }

    try {
      setMessage("⏳ Starting pitch analysis…");

      await addDoc(collection(db, "analysisJobs"), {
        userId: user.uid,
        pitchId: user.uid,
        status: "queued",
        createdAt: serverTimestamp(),
      });
    } catch {
      setMessage("❌ Failed to start analysis.");
    }
  };

  /* =========================
     UI
  ========================= */
  return (
    <div>
      <h1>Pitch</h1>
      <p
        style={{ color: "#64748b", maxWidth: 720 }}
        title="This is your primary source of information for evaluators. Clear, text-based pitches work best."
      >
        Upload your pitch deck and get AI-powered analysis on clarity, structure,
        and investor readiness.
      </p>

      {/* PLAN SUMMARY */}
      {planLoading ? (
        <p style={{ color: "#94a3b8" }}>Loading plan details…</p>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))",
            gap: 16,
            marginTop: 20,
          }}
        >
          <div
            className="card"
            style={{ padding: 16 }}
            title="Your current subscription plan determines monthly limits."
          >
            <div style={{ fontSize: 13, color: "#64748b" }}>Plan</div>
            <div style={{ fontSize: 22, fontWeight: 700 }}>
              {plan.toUpperCase()}
            </div>
          </div>

          <div
            className="card"
            style={{ padding: 16 }}
            title="How many pitch uploads you can still make this month."
          >
            <div style={{ fontSize: 13, color: "#64748b" }}>
              Remaining Uploads
            </div>
            <div style={{ fontSize: 22, fontWeight: 700 }}>
              {remainingUploads}
            </div>
          </div>

          <div
            className="card"
            style={{ padding: 16 }}
            title="How many AI analyses you can still run this month."
          >
            <div style={{ fontSize: 13, color: "#64748b" }}>
              Remaining Analyses
            </div>
            <div style={{ fontSize: 22, fontWeight: 700 }}>
              {remainingAnalyses}
            </div>
          </div>
        </div>
      )}

      {/* STATUS */}
      {(uploading || analyzing) && (
        <p style={{ marginTop: 16 }}>⏳ Processing…</p>
      )}

      {analysisJob && (
        <div
          style={{ marginTop: 12 }}
          title="Analysis runs asynchronously. You can navigate away and return later."
        >
          <strong>Status:</strong>{" "}
          {analysisJob.status === "queued" && "Queued ⏳"}
          {analysisJob.status === "processing" && "Processing ⏳"}
          {analysisJob.status === "completed" && "Completed ✅"}
          {analysisJob.status === "blocked" && "Blocked 🚫"}
          {analysisJob.status === "failed" && "Failed ❌"}
        </div>
      )}

      {/* CURRENT PITCH */}
      {currentPitch ? (
        <div
          className="card"
          style={{ marginTop: 24, padding: 20 }}
          title="This is the pitch evaluators will see."
        >
          <strong>{currentPitch.currentFileName}</strong>

          <button
            className="btn-primary"
            style={{
              marginTop: 16,
              display: "block",
              borderRadius: 999,
            }}
            onClick={analyzePitch}
            disabled={analyzing}
            title="AI analysis helps identify unclear claims and structural gaps. It does not judge your business."
          >
            Analyze pitch
          </button>

          {remainingAnalyses <= 0 && plan === "free" && (
            <p style={{ color: "#64748b", marginTop: 8 }}>
              Upgrade to analyze more pitches.
            </p>
          )}
        </div>
      ) : (
        <p
          style={{ marginTop: 16 }}
          title="Upload a pitch deck to begin analysis and generate claims."
        >
          No pitch uploaded yet.
        </p>
      )}

      {/* UPLOAD PANEL */}
      <div
        className="card"
        style={{ marginTop: 28, padding: 20, maxWidth: 720 }}
        title="Upload a text-based PDF. Visual-only decks cannot be analyzed."
      >
        <h3>Upload / Replace Pitch</h3>
        <input
          type="file"
          accept=".pdf"
          onChange={(e) => setFile(e.target.files[0])}
          style={{ marginTop: 10 }}
        />
        <button
          className="btn-secondary"
          style={{
            marginTop: 18,
            display: "block",
            borderRadius: 999,
          }}
          onClick={handleUpload}
          disabled={uploading}
          title="Replacing a pitch does not erase past evaluations, but future analyses use the latest file."
        >
          Upload pitch
        </button>
      </div>

      {message && <p style={{ marginTop: 16 }}>{message}</p>}
    </div>
  );
}

export default Pitch;
